import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { authService } from "@/services";
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar,
  Save,
  Edit,
  Camera,
  Shield,
  Key,
  Bell,
  Eye,
  EyeOff,
  Lock,
  Globe,
  CheckCircle2,
  AlertCircle,
  Upload
} from "lucide-react";

// Helper function to get role name from roleId
const getRoleName = (roleId?: number): string => {
  const roleMap: { [key: number]: string } = {
    1: "Customer",
    2: "Admin",
    3: "Staff"
  };
  return roleMap[roleId || 2] || "User";
};

export default function ProfileSettings() {
  const { t, i18n } = useTranslation();
  const { user, updateProfile } = useAuth();
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Profile form data - using localStorage for extended user data
  const getStoredUserData = () => {
    try {
      const stored = localStorage.getItem('user');
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          department: parsed.department || "",
          location: parsed.location || "",
          joinDate: parsed.joinDate || parsed.createdAt || "",
          lastLogin: parsed.lastLogin || "",
          avatar: parsed.avatar || ""
        };
      }
    } catch (e) {
      console.error('Error reading stored user data:', e);
    }
    return {
      department: "",
      location: "",
      joinDate: "",
      lastLogin: "",
      avatar: ""
    };
  };

  // Initialize profile data from user and localStorage
  const initializeProfileData = () => {
    const storedData = getStoredUserData();
    return {
      name: user?.name || "",
      email: user?.email || "",
      phone: user?.phone || "",
      role: getRoleName(user?.roleId),
      department: storedData.department,
      location: storedData.location,
      joinDate: storedData.joinDate,
      lastLogin: storedData.lastLogin,
      avatar: storedData.avatar
    };
  };

  // Profile form data
  const [profileData, setProfileData] = useState(initializeProfileData);

  // Update profile data when user changes
  useEffect(() => {
    if (user) {
      const storedData = getStoredUserData();
      setProfileData({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        role: getRoleName(user.roleId),
        department: storedData.department,
        location: storedData.location,
        joinDate: storedData.joinDate,
        lastLogin: storedData.lastLogin,
        avatar: storedData.avatar
      });
    }
  }, [user]);

  // Password change form
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });

  // Notification preferences
  const [notifications, setNotifications] = useState({
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false,
    bookingUpdates: true,
    promotions: true,
    systemAlerts: true
  });

  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await authService.updateProfile({
        name: profileData.name,
        email: profileData.email,
        language: i18n.language as 'en' | 'ar'
      });
      
      if (response.success && response.data) {
        // Update local user via updateProfile hook
        const updatedUser = await updateProfile({
          name: profileData.name,
          email: profileData.email,
          language: i18n.language as 'en' | 'ar'
        });
        
        // Store extended data in localStorage
        const extendedData = {
          ...updatedUser,
          department: profileData.department,
          location: profileData.location,
          avatar: profileData.avatar,
          joinDate: profileData.joinDate || updatedUser.createdAt,
          lastLogin: profileData.lastLogin
        };
        localStorage.setItem('user', JSON.stringify(extendedData));
        
        toast({
          title: t("success") || "Success",
          description: t("profileUpdatedSuccessfully") || "Profile updated successfully",
        });
        setIsEditing(false);
      } else {
        throw new Error(response.message || "Failed to update profile");
      }
    } catch (error: any) {
      console.error('Profile update error:', error);
      toast({
        title: t("error") || "Error",
        description: error.message || t("failedToUpdateProfile") || "Failed to update profile",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({
        title: t("error") || "Error",
        description: t("passwordsDoNotMatch") || "Passwords do not match",
        variant: "destructive",
      });
      return;
    }

    if (passwordData.newPassword.length < 8) {
      toast({
        title: t("error") || "Error",
        description: t("passwordTooShort") || "Password must be at least 8 characters",
        variant: "destructive",
      });
      return;
    }

    setPasswordLoading(true);
    try {
      const response = await authService.changePassword(
        passwordData.currentPassword,
        passwordData.newPassword
      );
      
      if (response.success) {
        toast({
          title: t("success") || "Success",
          description: t("passwordChangedSuccessfully") || "Password changed successfully",
        });
        setPasswordData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: ""
        });
      } else {
        throw new Error(response.message || "Failed to change password");
      }
    } catch (error: any) {
      console.error('Password change error:', error);
      toast({
        title: t("error") || "Error",
        description: error.message || t("failedToChangePassword") || "Failed to change password",
        variant: "destructive",
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleCancel = () => {
    const storedData = getStoredUserData();
    setProfileData({
      name: user?.name || "",
      email: user?.email || "",
      phone: user?.phone || "",
      role: getRoleName(user?.roleId),
      department: storedData.department,
      location: storedData.location,
      joinDate: storedData.joinDate,
      lastLogin: storedData.lastLogin,
      avatar: storedData.avatar
    });
    setIsEditing(false);
  };

  const handleInputChange = (field: string, value: string) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePasswordChange = (field: string, value: string) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNotificationToggle = (key: string, value: boolean) => {
    setNotifications(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleLanguageChange = (lang: 'en' | 'ar') => {
    i18n.changeLanguage(lang);
    toast({
      title: t("success") || "Success",
      description: t("languageChanged") || "Language changed successfully",
    });
  };

  const handleAvatarUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: t("error") || "Error",
        description: t("fileTooLarge") || "File size must be less than 5MB",
        variant: "destructive",
      });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      setProfileData(prev => ({
        ...prev,
        avatar: base64String
      }));
      toast({
        title: t("success") || "Success",
        description: t("imageUploaded") || "Image uploaded successfully",
      });
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-4 sm:space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight truncate">
            {t("profileSettings") || "Profile Settings"}
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            {t("manageYourProfileInformation") || "Manage your profile information and preferences"}
          </p>
        </div>
        <div className="flex gap-2">
          {isEditing ? (
            <>
              <Button variant="outline" onClick={handleCancel} disabled={loading}>
                {t("cancel") || "Cancel"}
              </Button>
              <Button onClick={handleSave} disabled={loading}>
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {t("saving") || "Saving..."}
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    {t("save") || "Save"}
                  </>
                )}
              </Button>
            </>
          ) : (
            <Button onClick={() => setIsEditing(true)}>
              <Edit className="mr-2 h-4 w-4" />
              {t("editProfile") || "Edit Profile"}
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-6 grid-cols-1 lg:grid-cols-3">
        {/* Profile Picture & Basic Info */}
        <div className="lg:col-span-1">
          <Card className="h-fit">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                {t("profilePicture") || "Profile Picture"}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col items-center space-y-4">
                <Avatar className="h-32 w-32 border-4 border-background shadow-lg">
                  <AvatarImage src={profileData.avatar} alt={profileData.name} />
                  <AvatarFallback className="text-3xl bg-gradient-to-br from-primary to-primary/60">
                    {profileData.name?.charAt(0) || "A"}
                  </AvatarFallback>
                </Avatar>
                {isEditing && (
                  <label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarUpload}
                      className="hidden"
                    />
                    <Button variant="outline" size="sm" asChild>
                      <span className="cursor-pointer">
                        <Upload className="mr-2 h-4 w-4" />
                        {t("uploadPhoto") || "Upload Photo"}
                      </span>
                    </Button>
                  </label>
                )}
              </div>
              
              <Separator />
              
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Shield className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">{t("role") || "Role"}:</span>
                  <span className="font-medium">{profileData.role || getRoleName(user?.roleId)}</span>
                </div>
                {profileData.joinDate && (
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">{t("joined") || "Joined"}:</span>
                    <span className="font-medium">
                      {profileData.joinDate ? new Date(profileData.joinDate).toLocaleDateString() : "N/A"}
                    </span>
                  </div>
                )}
                {profileData.lastLogin && (
                  <div className="flex items-center gap-2 text-sm">
                    <Key className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">{t("lastLogin") || "Last Login"}:</span>
                    <span className="font-medium">
                      {profileData.lastLogin ? new Date(profileData.lastLogin).toLocaleDateString() : "N/A"}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Profile Details */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t("profileDetails") || "Profile Details"}</CardTitle>
              <CardDescription>
                {t("updateYourProfileInformation") || "Update your personal information"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 grid-cols-1 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name" className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    {t("fullName") || "Full Name"}
                  </Label>
                  <Input
                    id="name"
                    value={profileData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    disabled={!isEditing}
                    placeholder={t("enterFullName") || "Enter your full name"}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    {t("email") || "Email"}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={profileData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    disabled={!isEditing}
                    placeholder={t("enterEmail") || "Enter your email"}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="phone" className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    {t("phone") || "Phone"}
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    disabled={true}
                    placeholder={t("enterPhone") || "Enter your phone number"}
                    className="bg-muted"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t("phoneCannotBeChanged") || "Phone number cannot be changed"}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="department">{t("department") || "Department"}</Label>
                  <Input
                    id="department"
                    value={profileData.department}
                    onChange={(e) => handleInputChange('department', e.target.value)}
                    disabled={!isEditing}
                    placeholder={t("enterDepartment") || "Enter your department"}
                  />
                </div>
                
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="location" className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    {t("location") || "Location"}
                  </Label>
                  <Input
                    id="location"
                    value={profileData.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    disabled={!isEditing}
                    placeholder={t("enterLocation") || "Enter your location"}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Security Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                {t("security") || "Security"}
              </CardTitle>
              <CardDescription>
                {t("changeYourPassword") || "Change your password to keep your account secure"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="currentPassword">{t("currentPassword") || "Current Password"}</Label>
                <div className="relative">
                  <Input
                    id="currentPassword"
                    type={showCurrentPassword ? "text" : "password"}
                    value={passwordData.currentPassword}
                    onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                    placeholder={t("enterCurrentPassword") || "Enter current password"}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="newPassword">{t("newPassword") || "New Password"}</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showNewPassword ? "text" : "password"}
                    value={passwordData.newPassword}
                    onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                    placeholder={t("enterNewPassword") || "Enter new password"}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t("confirmPassword") || "Confirm Password"}</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={passwordData.confirmPassword}
                    onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                    placeholder={t("confirmNewPassword") || "Confirm new password"}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              
              <Button 
                onClick={handleChangePassword} 
                disabled={passwordLoading || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                className="w-full"
              >
                {passwordLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {t("changingPassword") || "Changing Password..."}
                  </>
                ) : (
                  <>
                    <Lock className="mr-2 h-4 w-4" />
                    {t("changePassword") || "Change Password"}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Language Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                {t("languageSettings") || "Language Settings"}
              </CardTitle>
              <CardDescription>
                {t("selectYourPreferredLanguage") || "Select your preferred language"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <Button
                  variant={i18n.language === 'en' ? "default" : "outline"}
                  onClick={() => handleLanguageChange('en')}
                  className="flex-1"
                >
                  {i18n.language === 'en' && <CheckCircle2 className="mr-2 h-4 w-4" />}
                  English
                </Button>
                <Button
                  variant={i18n.language === 'ar' ? "default" : "outline"}
                  onClick={() => handleLanguageChange('ar')}
                  className="flex-1"
                >
                  {i18n.language === 'ar' && <CheckCircle2 className="mr-2 h-4 w-4" />}
                  العربية
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Notification Preferences */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                {t("notificationPreferences") || "Notification Preferences"}
              </CardTitle>
              <CardDescription>
                {t("manageYourNotificationSettings") || "Choose how you want to be notified"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>{t("emailNotifications") || "Email Notifications"}</Label>
                  <p className="text-sm text-muted-foreground">
                    {t("emailNotificationsDesc") || "Receive notifications via email"}
                  </p>
                </div>
                <Switch
                  checked={notifications.emailNotifications}
                  onCheckedChange={(checked) => handleNotificationToggle('emailNotifications', checked)}
                />
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>{t("pushNotifications") || "Push Notifications"}</Label>
                  <p className="text-sm text-muted-foreground">
                    {t("pushNotificationsDesc") || "Receive push notifications"}
                  </p>
                </div>
                <Switch
                  checked={notifications.pushNotifications}
                  onCheckedChange={(checked) => handleNotificationToggle('pushNotifications', checked)}
                />
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>{t("smsNotifications") || "SMS Notifications"}</Label>
                  <p className="text-sm text-muted-foreground">
                    {t("smsNotificationsDesc") || "Receive SMS notifications"}
                  </p>
                </div>
                <Switch
                  checked={notifications.smsNotifications}
                  onCheckedChange={(checked) => handleNotificationToggle('smsNotifications', checked)}
                />
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>{t("bookingUpdates") || "Booking Updates"}</Label>
                  <p className="text-sm text-muted-foreground">
                    {t("receiveBookingUpdates") || "Get notified about booking changes"}
                  </p>
                </div>
                <Switch
                  checked={notifications.bookingUpdates}
                  onCheckedChange={(checked) => handleNotificationToggle('bookingUpdates', checked)}
                />
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>{t("promotions") || "Promotions"}</Label>
                  <p className="text-sm text-muted-foreground">
                    {t("receivePromotionalOffers") || "Get promotional offers and discounts"}
                  </p>
                </div>
                <Switch
                  checked={notifications.promotions}
                  onCheckedChange={(checked) => handleNotificationToggle('promotions', checked)}
                />
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>{t("systemAlerts") || "System Alerts"}</Label>
                  <p className="text-sm text-muted-foreground">
                    {t("receiveSystemAlerts") || "Important system announcements"}
                  </p>
                </div>
                <Switch
                  checked={notifications.systemAlerts}
                  onCheckedChange={(checked) => handleNotificationToggle('systemAlerts', checked)}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
